import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';
import { useLocations } from '@/hooks/useLocations';
import {
  getGeneralSettings,
  saveGeneralSettings,
  resetGeneralSettings,
  type GeneralSettings,
  type DateFormatPreference,
  DATE_FORMAT_OPTIONS,
} from '@/lib/settings';
import {
  RotateCcw,
  Save,
  Info,
  Building2,
  MapPin,
  Globe,
} from 'lucide-react';

export default function SettingsGeneralTab() {
  const [settings, setSettings] = useState<GeneralSettings>(getGeneralSettings);
  const [saved, setSaved] = useState(false);
  const { success: toastSuccess } = useToast();
  const { data: locations = [] } = useLocations();

  useEffect(() => {
    setSettings(getGeneralSettings());
  }, []);

  const handleSave = () => {
    saveGeneralSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    toastSuccess('Settings saved', 'General settings have been updated.');
  };

  const handleReset = () => {
    setSettings(resetGeneralSettings());
    toastSuccess('Settings reset', 'General settings have been reset to defaults.');
  };

  const updateSetting = <K extends keyof GeneralSettings>(
    key: K,
    value: GeneralSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const locationOptions = locations.map((loc) => ({
    value: loc.location_id || '',
    label: `${loc.code} - ${loc.name}`,
  }));

  return (
    <div className="space-y-6">
      {/* Organization Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organization
          </CardTitle>
          <CardDescription>
            Basic information about your organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hospitalName">Hospital / Clinic Name</Label>
            <Input
              id="hospitalName"
              placeholder="Enter hospital name"
              value={settings.hospitalName}
              onChange={(e) => updateSetting('hospitalName', e.target.value)}
            />
            <p className="text-xs text-text-muted">
              This name will be displayed throughout the application
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Regional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Regional Settings
          </CardTitle>
          <CardDescription>
            Configure regional preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={settings.currency}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-text-muted">
                Currency is locked to IDR (Indonesian Rupiah)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateFormat">Date Format</Label>
              <select
                id="dateFormat"
                value={settings.dateFormat}
                onChange={(e) => updateSetting('dateFormat', e.target.value as DateFormatPreference)}
                className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full"
              >
                {DATE_FORMAT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Input
              id="timezone"
              value={settings.timezone}
              disabled
              className="bg-muted w-full md:w-64"
            />
            <p className="text-xs text-text-muted">
              Timezone is locked to Asia/Jakarta (WIB)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Default Values */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Defaults
          </CardTitle>
          <CardDescription>
            Default values for new transactions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="defaultLocation">Default Location</Label>
            <select
              id="defaultLocation"
              value={settings.defaultLocationId || ''}
              onChange={(e) => updateSetting('defaultLocationId', e.target.value || undefined)}
              className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-64"
            >
              <option value="">None</option>
              {locationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted">
              Default location for new requisitions and orders
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button onClick={handleSave}>
          <Save className="mr-2 h-4 w-4" />
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <p className="font-medium text-text-primary mb-1">Settings are stored locally</p>
              <p>
                These settings are saved in your browser and will persist across sessions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
